import { Injectable, Inject } from '@angular/core';
import { Action, Store, select } from '@ngrx/store';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { take, map, catchError, withLatestFrom, switchMap, filter, auditTime, mergeMap, tap, concatMap } from 'rxjs/operators';

import * as actions from '../actions';
import * as selectors from '../selectors';
import * as Utils from '@shared/core/utils';
import * as Services from '@shared/core/services';
import * as Tokens from '@shared/core/tokens';
import * as Models from '@shared/core/models';

import { OnlineOrdersMapper } from '@shared/core/mappers/online-orders.shared.mapper';

@Injectable()
export class OnlineOrdersEffects {
    public saveOrderConfirmationUrl$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderSaveConfirmationUrlRequest),
            switchMap(({ orderGUID, url }) => {
                const accessToken = orderGUID;
                const model: Models.OnlineOrderUrlModel = new Models.OnlineOrderUrlModel(accessToken, url);

                return this._onlineOrdersService.insertOnlineOrderUrl(model).pipe(
                    map((payload) =>
                        actions.OnlineOrderSaveConfirmationUrlSuccessRequest({
                            orderGUID,
                            url,
                            payload,
                        }),
                    ),
                    catchError((ex) => {
                        console.error("Unable to save order's confirmation url", ex);

                        return of(
                            actions.OnlineOrderSaveConfirmationUrlErrorRequest({
                                orderGUID,
                                url,
                                ex,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    public onOnlineOrderCreateDeliveryRequest$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderCreateDeliveryRequest),
            withLatestFrom(this._store.pipe(select(selectors.getCurrentDeliveryProvider))),
            switchMap(([{ orderGUID }, deliveryProviderId]) =>
                this._onlineOrdersService.createDelivery$(orderGUID, deliveryProviderId).pipe(
                    map(() => actions.OnlineOrderCreateDeliverySuccessRequest()),
                    catchError((err) => {
                        console.error(err);

                        return [actions.OnlineOrderCreateDeliveryErrorRequest({ orderGUID })];
                    }),
                ),
            ),
        ),
    );

    public onOnlineOrderCreateSuccessForDeliveryRequest$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderCreateSuccessRequest),
            withLatestFrom(this._store.pipe(select(selectors.isCartCollectionTypeDelivery)), this._store.pipe(select(selectors.isDeliveryProviderSupported))),
            filter(([, isDelivery, isDeliveryProviderSupported]) => isDelivery && isDeliveryProviderSupported),
            map(([action]) => actions.OnlineOrderCreateDeliveryRequest({ orderGUID: action.payload.AccessToken })),
        ),
    );

    public onOnlineOrderCreateSuccessRequest$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderCreateSuccessRequest),
            map((action) => actions.MemberSetMemberIdAfterCreateOnlineOrder({ memberId: action.payload.MemberId })),
        ),
    );

    public onOnlineOrderCreateDeliveryErrorRequest: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderCreateDeliveryErrorRequest),
            map((action) => actions.OnlineOrderCancelRequest({ orderGUID: action.orderGUID, updateHistoryOrder: true, sendNotification: false })),
        ),
    );

    public onOnlineOrderCreateDuplicationCheckForCard: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderCreateDuplicationCheckForCard),
            switchMap((action) => {
                const { OrderGUID: orderGUID, TransactionId: transactionId } = action.previousOrderInfo;

                return this._paymentService.getPaymentStatus(transactionId).pipe(
                    mergeMap((payment) => this._getOrderStreamBasedOnAuth$(orderGUID).pipe(map((orderStatus) => ({ payment, orderStatus })))),
                    map(({ payment, orderStatus }) => ({
                        orderStatus,
                        action,
                        duplicateFlags: this._getDuplicateOrderFlags(action, orderStatus.Status, payment),
                    })),
                );
            }),
            switchMap(({ action, duplicateFlags }) => {
                const { isRecentSameOrder, isDuringPaymentProcedure, isAlreadyPayed, isImproperOrder } = duplicateFlags;
                const orderGUID = action.previousOrderInfo.OrderGUID;

                if (isRecentSameOrder && isDuringPaymentProcedure) {
                    this._store.dispatch(actions.PaymentStepPaymentStatusCheck({ TransactionId: action.previousOrderInfo.TransactionId, OrderGUID: orderGUID }));

                    this._modalsServicService.show({
                        type: 'order-unprocessed-payment',
                        params: {
                            orderModel: action.model,
                            transactionId: action.previousOrderInfo.TransactionId,
                        },
                    });

                    return [];
                }

                if (isRecentSameOrder && isAlreadyPayed) {
                    this._modalsServicService.show({
                        type: 'order-duplicate-warn',
                        params: {
                            orderModel: action.model,
                        },
                    });

                    return [];
                }

                if (isImproperOrder) {
                    this._onlineOrdersService.cancelOnlineOrder(action.model.AccessToken, false);

                    return [];
                }

                this._onlineOrdersService.clearSavedOrderInfo();
                this._onlineOrdersService.createNewOnlineOrderWithBaseInfoSave(action.model);

                return [];
            }),
        ),
    );

    public onOnlineOrderCreateDuplicationCheckForPayWithAccountCharge: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderCreateDuplicationCheckForPayWithAccountCharge),
            switchMap((action) => {
                const orderGUID = action.previousOrderInfo.OrderGUID;

                return this._onlineOrdersService.requestOrderTracking$(orderGUID).pipe(
                    catchError((error) => {
                        console.error('Error fetching order status', error);

                        return [];
                    }),
                    map((orderStatus) => ({ action, orderStatus })),
                );
            }),
            map(({ action, orderStatus }) => ({ action, duplicateFlags: this._getDuplicateOrderFlags(action, orderStatus) })),
            switchMap(({ action, duplicateFlags }) => {
                if (!duplicateFlags) return [];
                const { isSameGross, isRecentSameOrder, isAlreadyPayed } = duplicateFlags;
                if (isSameGross && isRecentSameOrder && isAlreadyPayed) {
                    this._modalsServicService.show({
                        type: 'order-duplicate-warn',
                        params: {
                            orderModel: action.model,
                        },
                    });

                    return [];
                }
                this._onlineOrdersService.createNewOnlineOrderWithBaseInfoSave(action.model);

                return [];
            }),
        ),
    );

    public onOnlineOrderPointsRedemptionRequest$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderPointsRedemptionRequest),
            withLatestFrom(this._store.select(selectors.getOnlineOrderState)),
            switchMap(([, orderState]) => {
                const orderModel = orderState.data;

                return this._onlineOrdersService.redeemOrderByPoints(orderModel).pipe(map((response) => actions.OnlineOrderPointsRedemptionSuccessRequest({ payload: response })));
            }),
            catchError((ex) => {
                console.error(ex);

                return of(actions.OnlineOrderPointsRedemptionErrorRequest());
            }),
        ),
    );

    public onOnlineOrderPointsRedemptionSuccessRequest$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderPointsRedemptionSuccessRequest),
            switchMap(() => {
                this._onlineOrdersService.placeOrderWithPaymentProvider();

                return [];
            }),
        ),
    );

    public onOnlineOrderCreateRequest$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderCreateRequest),
            withLatestFrom(
                this._store.select(selectors.getGuestData),
                this._store.select(selectors.getCurrentMember),
                this._store.select(selectors.isCartZeroPrice),
                this._store.select(selectors.isPayInStoreSelected),
                this._store.select(selectors.isAccountChargeSelected),
            ),
            switchMap(([, guestData, memberData, isZeroPricedOrder, isPayInStoreSelected, isAccountChargeSelected]) =>
                this._store.pipe(
                    select(selectors.isOrderRecalculating),
                    filter((orderRecalculating) => !orderRecalculating),
                    auditTime(100),
                    take(1),
                    withLatestFrom(
                        this._store.pipe(select(selectors.getOnlineOrderRecalcData)),
                        this._store.pipe(select(selectors.getOnlineOrderState)),
                        this._store.pipe(select(selectors.getLoyaltyAppId)),
                        this._store.pipe(select(selectors.getOnlineOrder)),
                    ),
                    switchMap(([, calculatedOrder, orderState, LoyaltyAppId, onlineOrderData]) => {
                        if (calculatedOrder.hasFailed || !calculatedOrder.data) throw new Error('Invalid recalculated order data');

                        const MemberId = memberData ? memberData.UserId || memberData.MemberId : null;

                        const Status =
                            isZeroPricedOrder || isPayInStoreSelected || this._config.demoMode === true ? OLO.Enums.ONLINE_ORDER_STATUS.VALIDATED : onlineOrderData.Status;

                        const partialMember = guestData
                            ? {
                                  ...guestData,
                                  MobileNumberCountryID: guestData.MobilePhoneCountryId,
                              }
                            : null;

                        const model: OLO.DTO.OnlineOrderDetailedBusinessModel = {
                            ...calculatedOrder.data,
                            Id: null,
                            MemberId: MemberId || null,
                            PartialMember: partialMember,
                            LoyaltyAppId,
                            Status,
                            SendReceiptOnEmail: this._config.onlineOrders?.sendAutoReceiptEmail === true,
                            ReceiptNotificationEmailAdresses: [guestData?.Email || memberData?.Email].filter((obj) => obj),
                            ...Utils.OnlineOrders.extendWithOrderTypeDisclaimers(onlineOrderData, orderState.orderType),
                        };

                        const previousOrderInfo = Utils.Storage.getEncryptedItem(OLO.Enums.CART_STORAGE.ORDER) as OLO.DTO.OnlineOrdersPreviousOrder;

                        switch (true) {
                            case !!(previousOrderInfo?.OrderGUID && previousOrderInfo?.TransactionId && previousOrderInfo?.MemberId === MemberId):
                                return of(actions.OnlineOrderCreateDuplicationCheckForCard({ model: model, previousOrderInfo: previousOrderInfo }));
                            case !!(isAccountChargeSelected && previousOrderInfo?.OrderGUID && previousOrderInfo?.MemberId === MemberId):
                                return of(actions.OnlineOrderCreateDuplicationCheckForPayWithAccountCharge({ model: model, previousOrderInfo: previousOrderInfo }));
                            default:
                                return this._onlineOrdersService.createNewOnlineOrder(model).pipe(
                                    map((payload) => {
                                        this._onlineOrdersService.saveOrderBaseInfo({
                                            OrderedDate: payload.OrderedDate,
                                            TotalGrossValue: payload.TotalGrossValue,
                                            OrderGUID: payload.AccessToken,
                                            ProductIds: payload.Items.map((item) => item.PLU),
                                            MemberId: payload.MemberId,
                                        });

                                        return actions.OnlineOrderCreateSuccessRequest({ payload });
                                    }),
                                );
                        }
                    }),
                    catchError((ex) => {
                        console.error(ex);

                        return of(actions.OnlineOrderCreateErrorRequest({ order: null, ex }));
                    }),
                ),
            ),
        ),
    );

    public onOnlineOrderSendConfirmationEmail$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderSendConfrimationEmailRequest),
            switchMap((action) =>
                this._onlineOrdersService.sendEmailWithOrderConfirmation(action.orderGUID).pipe(
                    map((result) => (result ? actions.OnlineOrderSendConfrimationEmailSuccessRequest({ result }) : actions.OnlineOrderSendConfrimationEmailErrorRequest({}))),
                    catchError((ex) => of(actions.OnlineOrderSendConfrimationEmailErrorRequest({ ex }))),
                ),
            ),
        ),
    );

    public onOnlineOrderUpdateSpecialInstructions$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderUpdateSpecialInstructions),
            map(() => actions.OnlineOrderRecalculateRequest()),
        ),
    );

    public onOnlineOrderRecalculateRequest$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderRecalculateRequest),
            switchMap(({ shouldConfirmPreparationTime = true }) =>
                this._store.pipe(
                    select(selectors.isDownloadingAnyOrderTypes),
                    filter((isDownloading) => isDownloading === false),
                    take(1),
                    withLatestFrom(
                        this._store.select(selectors.getCart),
                        this._store.select(selectors.getCartTotalValue),
                        this._store.select(selectors.getCurrentMember),
                        this._store.pipe(select(selectors.getOnlineOrderState)),
                        this._store.pipe(select(selectors.getLoyaltyAppId)),
                        this._store.pipe(select(selectors.getVirtualLocations)),
                        this._store.pipe(select(selectors.getOnlineOrderRecalcData)),
                    ),
                    switchMap(([, cart, , member, orderState, loyaltyAppId, virtualLocations, recalculateData]) => {
                        const isDelivery = Utils.OnlineOrders.detectOrderCollectionTypeGroup(orderState.data) === OLO.Enums.COLLECTION_TYPE.DELIVERY;
                        const isSecondRecalculate = isDelivery && shouldConfirmPreparationTime === false;
                        const tempOrderModel = Utils.OnlineOrders.convertCart(
                            this._config.onlineOrders.saleName,
                            cart,
                            cart.pickupTime,
                            {
                                MemberId: member ? member.MemberId : null,
                                RemoveModifiers: this._config.onlineOrders && this._config.onlineOrders.allowModifiers === false,
                                VirtualLocations: Utils.OnlineOrders.extendOnlineOrderWithVirtualLocations(cart, virtualLocations),
                            },
                            virtualLocations,
                        );

                        const deliveryObject: APIv3_SNAPSHOT.OnlineOrderDeliveryDetailsBusinessModel = isDelivery
                            ? {
                                  ...orderState.data?.DeliveryDetails,
                                  DesiredDeliveryTime: isSecondRecalculate ? orderState.data?.DeliveryDetails.DesiredDeliveryTime : tempOrderModel.PickUpDate,
                                  ...(isSecondRecalculate ? recalculateData.data.DeliveryDetails : {}),
                              }
                            : null;

                        const surcharges = isSecondRecalculate ? recalculateData?.data?.Surcharges : tempOrderModel.Surcharges;

                        const orderModel: OLO.DTO.OnlineOrderDetailedBusinessModel = {
                            ...tempOrderModel,
                            ...Utils.OnlineOrders.extendWithOrderTypeDisclaimers(tempOrderModel, orderState.orderType),
                            LoyaltyAppId: loyaltyAppId,
                            ActivatedVouchers: orderState.data?.ActivatedVouchers || cart.activatedVouchers,
                            IsDelivery: isDelivery,
                            DeliveryDetails: deliveryObject,
                            DesiredPickUpDate: isSecondRecalculate ? recalculateData.data?.DesiredPickUpDate : tempOrderModel.DesiredPickUpDate,
                            PickUpDate: isSecondRecalculate ? recalculateData.data?.PickUpDate : tempOrderModel.PickUpDate,
                            EstimatedPreparationTimeInMinutes: isSecondRecalculate
                                ? recalculateData.data?.EstimatedPreparationTimeInMinutes
                                : tempOrderModel.EstimatedPreparationTimeInMinutes,
                            ExtraInstructions: orderState?.data?.ExtraInstructions,
                            Surcharges: surcharges,
                        };

                        return this._onlineOrdersService.recalculateOnlineOrder(orderModel).pipe(
                            tap(() => {
                                if (isDelivery && shouldConfirmPreparationTime === true) {
                                    this._store.dispatch(actions.OnlineOrderShouldPerformQuoteDleiveryCheck());
                                }
                            }),
                            concatMap((payload) => {
                                const actionsToDispatch: Action[] = [actions.OnlineOrderRecalculateSuccessRequest({ payload })];

                                if (shouldConfirmPreparationTime) {
                                    actionsToDispatch.push(actions.OnlineOrderPreparationTimeConfirmRequest({ payload }));
                                }

                                return actionsToDispatch;
                            }),
                            catchError((ex) => [actions.OnlineOrderRecalculateErrorRequest({ order: orderModel, ex })]),
                        );
                    }),
                ),
            ),
        ),
    );

    public onConfirmPreparationTimeRequest$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderPreparationTimeConfirmRequest),
            switchMap((action) =>
                this._onlineOrdersService.confirmPreparationTimeForOnlineOrder$(action.payload).pipe(
                    concatMap((payload) => [
                        actions.OnlineOrderPreparationTimeConfirmSuccessRequest({ payload }),
                        ...(payload.DeliveryDetails ? [actions.OnlineOrderQuoteDeliveryRequest({ address: payload.DeliveryDetails, setCartDeliveryAddress: true })] : []),
                    ]),
                    catchError((ex) => [actions.OnlineOrderPreparationTimeConfirmErrorRequest({ order: action.payload, ex })]),
                ),
            ),
        ),
    );

    public handleSecondPaymentOptionBehavior$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderRecalculateSuccessRequest),
            withLatestFrom(
                this._store.select(selectors.isPayByPointsSelected),
                this._store.select(selectors.canFulfillPaymentWithPointsOnly),
                this._store.select(selectors.getActiveCardId),
            ),
            switchMap(([action, isPayByPointsSelected, canFulfillPaymentWithPointsOnly, activeCardId]) => {
                const actionsToDispatch: Action[] = [];

                if (!isPayByPointsSelected) return actionsToDispatch;

                const shouldRemoveSecondPaymentOption = canFulfillPaymentWithPointsOnly && isPayByPointsSelected && activeCardId !== OLO.Enums.PAYMENT_VENDOR_SERVICE.PAY_BY_POINTS;

                if (shouldRemoveSecondPaymentOption) {
                    actionsToDispatch.push(actions.SelectActiveCreditCardId({ cardId: OLO.Enums.PAYMENT_VENDOR_SERVICE.PAY_BY_POINTS }));
                }

                return actionsToDispatch;
            }),
        ),
    );

    public handlePreparationTime$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderPreparationTimeConfirmSuccessRequest),
            withLatestFrom(this._store.select(selectors.getOnlineOrderState)),
            switchMap(([action, orderState]) => {
                const orderModel = orderState.data;
                const payload = action.payload;

                if (Utils.Dates.datesDiffInMinutes(new Date(orderModel.PickUpDate), new Date(payload.PickUpDate), 'up') === 0) {
                    return [];
                }

                return [actions.OnlineOrderUpdatePickupTime({ pickupTime: payload.PickUpDate })];
            }),
        ),
    );

    public onOnlineOrderAddVoucherRequest$: Observable<any> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderAddVoucherRequest),
            switchMap((AddVoucherRequestAction) =>
                this._store.pipe(
                    select(selectors.isDownloadingAnyOrderTypes),
                    filter((isDownloading) => isDownloading === false),
                    take(1),
                    withLatestFrom(
                        this._store.select(selectors.getOnlineOrderVoucherCode),
                        this._store.select(selectors.getCart),
                        this._store.select(selectors.getCurrentMember),
                        this._store.pipe(select(selectors.getOnlineOrderState)),
                        this._store.pipe(select(selectors.getLoyaltyAppId)),
                        this._store.pipe(select(selectors.getVirtualLocations)),
                    ),
                    switchMap(([, VoucherCode, cart, member, orderState, LoyaltyAppId, virtualLocations]) => {
                        const _tempOrderModel = Utils.OnlineOrders.convertCart(
                            this._config.onlineOrders.saleName,
                            cart,
                            cart.pickupTime,
                            {
                                MemberId: member ? member.MemberId : null,
                                RemoveModifiers: this._config.onlineOrders && this._config.onlineOrders.allowModifiers === false,
                                VirtualLocations: Utils.OnlineOrders.extendOnlineOrderWithVirtualLocations(cart, virtualLocations),
                            },
                            virtualLocations,
                        );
                        const orderModel: OLO.DTO.ActivateVoucherCommand = {
                            Order: {
                                ..._tempOrderModel,
                                ...Utils.OnlineOrders.extendWithOrderTypeDisclaimers(_tempOrderModel, orderState.orderType),
                                LoyaltyAppId,
                                ActivatedVouchers: orderState.data?.ActivatedVouchers || cart.activatedVouchers,
                            },
                            VoucherCode,
                            ActivatedEntityId: null, // TODO Check this value
                        };

                        return this._onlineOrdersService.addVoucherOnlineOrder(orderModel).pipe(
                            switchMap((payload) => {
                                this._modalsServicService.close(AddVoucherRequestAction.id);

                                return [actions.OnlineOrderAddVoucherSuccessRequest({ code: VoucherCode, payload }), actions.CartAddActivatedVoucher({ payload })];
                            }),
                            catchError((ex) => of(actions.OnlineOrderAddVoucherErrorRequest({ order: orderModel.Order, ex, staticText: this._t }))),
                        );
                    }),
                ),
            ),
        ),
    );

    public onOnlineOrderRemoveVoucherRequest$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderRemoveVoucherRequest),
            switchMap(() =>
                this._store.pipe(
                    select(selectors.isDownloadingAnyOrderTypes),
                    filter((isDownloading) => isDownloading === false),
                    take(1),
                    withLatestFrom(
                        this._store.select(selectors.getActiveVoucher),
                        this._store.select(selectors.getCart),
                        this._store.select(selectors.getCurrentMember),
                        this._store.pipe(select(selectors.getOnlineOrderState)),
                        this._store.pipe(select(selectors.getLoyaltyAppId)),
                        this._store.pipe(select(selectors.getVirtualLocations)),
                    ),
                    switchMap(([, voucher, cart, member, orderState, LoyaltyAppId, virtualLocations]) => {
                        if (voucher) {
                            const tempOrderModel = Utils.OnlineOrders.convertCart(
                                this._config.onlineOrders.saleName,
                                cart,
                                cart.pickupTime,
                                {
                                    MemberId: member ? member.MemberId : null,
                                    RemoveModifiers: this._config.onlineOrders && this._config.onlineOrders.allowModifiers === false,
                                    VirtualLocations: Utils.OnlineOrders.extendOnlineOrderWithVirtualLocations(cart, virtualLocations),
                                },
                                virtualLocations,
                            );

                            const orderModel: OLO.DTO.DeactivateVoucherCommand = {
                                Order: {
                                    ...tempOrderModel,
                                    ...Utils.OnlineOrders.extendWithOrderTypeDisclaimers(tempOrderModel, orderState.orderType),
                                    LoyaltyAppId,
                                    ActivatedVouchers: orderState.data?.ActivatedVouchers || cart.activatedVouchers,
                                },
                                VoucherCode: voucher?.VoucherCode,
                            };

                            return this._onlineOrdersService.removeVoucherOnlineOrder(orderModel).pipe(
                                switchMap((payload) => [actions.OnlineOrderRemoveVoucherSuccessRequest({ payload }), actions.CartRemoveActivatedVoucher()]),
                                catchError((ex) => of(actions.OnlineOrderRemoveVoucherErrorRequest({ order: orderModel.Order, ex }))),
                            );
                        } else {
                            return [];
                        }
                    }),
                ),
            ),
        ),
    );

    public onlineOrderRequest$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderRequest),
            switchMap(({ orderGUID }) =>
                this._onlineOrdersService.getOnlineOrder$(orderGUID).pipe(
                    map((payload) => actions.OnlineOrderSuccessRequest({ payload })),
                    catchError((ex) => of(actions.OnlineOrderErrorRequest({ orderGUID, ex }))),
                ),
            ),
        ),
    );

    public updateOnlineOrderRequest$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderUpdateRequest),
            switchMap((onlineOrderUpdatePayload) =>
                this._onlineOrdersService.updateOnlineOrder(onlineOrderUpdatePayload.order).pipe(
                    switchMap(() => [
                        actions.OnlineOrderUpdateSuccessRequest({ payload: onlineOrderUpdatePayload.order }),
                        actions.HistoryOrderRequest({ orderGUID: onlineOrderUpdatePayload.order.AccessToken }),
                    ]),
                    catchError((ex) => of(actions.OnlineOrderUpdateErrorRequest({ orderGUID: onlineOrderUpdatePayload.order.AccessToken, ex }))),
                ),
            ),
        ),
    );

    public cancelOnlineOrderRequest$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderCancelRequest),
            switchMap((onlineOrderCancelPayload) =>
                this._onlineOrdersService.cancelOnlineOrder(onlineOrderCancelPayload.orderGUID, onlineOrderCancelPayload.sendNotification).pipe(
                    map((payload) =>
                        actions.OnlineOrderCancelSuccessRequest({
                            payload,
                            orderGUID: onlineOrderCancelPayload.orderGUID,
                            updateHistoryOrder: onlineOrderCancelPayload.updateHistoryOrder,
                        }),
                    ),
                    catchError((ex) => of(actions.OnlineOrderCancelErrorRequest({ orderGUID: onlineOrderCancelPayload.orderGUID, ex }))),
                ),
            ),
        ),
    );

    public sendOrderReceipt$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderSendEmailReceiptRequest),
            switchMap(({ orderGUID }) =>
                this._onlineOrdersService.sendOnlineOrderReceipt(orderGUID).pipe(
                    map((response) =>
                        response ? actions.OnlineOrderSendEmailReceiptSuccessRequest({ orderGUID }) : actions.OnlineOrderSendEmailReceiptErrorRequest({ orderGUID }),
                    ),
                    catchError((ex) => {
                        console.log('Send order receipt error', ex);

                        return of(actions.OnlineOrderSendEmailReceiptErrorRequest({ orderGUID, ex }));
                    }),
                ),
            ),
        ),
    );

    public resetReceiptEmailSendButton$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderSendEmailReceiptSuccessRequest),
            auditTime(1000),
            switchMap(() => of(actions.OnlineOrderSendEmailReceiptSuccessReset())),
        ),
    );

    public onCartDeliveryAddressSet$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.CartSetDeliveryAddress),
            withLatestFrom(this._store.select(selectors.getOnlineOrder), this._store.select(selectors.getCartLocationNo)),
            switchMap(([action, onlineOrder, locationNo]) =>
                this._store.pipe(
                    select(selectors.getLocationDetails(locationNo)),
                    switchMap((location) => {
                        if (!onlineOrder || !location) return [];

                        const orderType = location.OrderTypes.find((obj) => obj.Id === onlineOrder.OrderTypeId);

                        const address = {
                            ...action.address,
                            DesiredDeliveryTime: action.address?.DesiredDeliveryTime || onlineOrder?.PickUpDate,
                            EstimatedDeliveryTime: orderType.AvailablePickupTimes.NextTickInMinutes,
                        };

                        return [actions.OnlineDeliveryAddressSet({ address })];
                    }),
                ),
            ),
        ),
    );

    public requestOrderTracking$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderTrackingRequest),
            switchMap(({ orderGUID }) =>
                this._onlineOrdersService.requestOrderTracking$(orderGUID).pipe(
                    map((orderTracking) => actions.OnlineOrderTrackingSuccessRequest({ orderTracking })),
                    catchError((ex) => {
                        console.error('Order tracking not returned', ex);

                        return [actions.OnlineOrderTrackingErrorRequest({ orderGUID, ex })];
                    }),
                ),
            ),
        ),
    );

    public clearSavedOrderData$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(actions.OnlineOrderClearSavedOrderData),
                tap(() => {
                    this._onlineOrdersService.clearSavedOrderInfo();
                }),
            ),
        { dispatch: false },
    );

    public onOnlineOrderQuoteDeliveryRequest$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderQuoteDeliveryRequest),
            switchMap(({ address, setCartDeliveryAddress }) =>
                this._store.pipe(
                    take(1),
                    withLatestFrom(
                        this._store.pipe(select(selectors.getOnlineOrderRecalcData)),
                        this._store.pipe(select(selectors.isGuestMember)),
                        this._store.pipe(select(selectors.getGuestData)),
                        this._store.pipe(select(selectors.isDeliveryProviderSupported)),
                    ),
                    switchMap(([_, onlineOrder, isGuestMember, guestData, shouldSendQuote]) => {
                        const onlineOrderModel = {
                            ...onlineOrder.data,
                            PartialMember: isGuestMember
                                ? {
                                      ...guestData,
                                      MobileNumberCountryID: guestData.MobilePhoneCountryId,
                                  }
                                : onlineOrder.data.PartialMember,
                        };

                        const model: APIv3.QuoteDeliveryCommand = {
                            DeliveryProvider: this._config.onlineOrders.deliveryProvider,
                            Order: {
                                ...OnlineOrdersMapper.mapRecalculateOnlineOrderPOSTRequest(onlineOrderModel),
                                DeliveryDetails: OnlineOrdersMapper.mapDeliveryAddressForDeliveryBussinessModel(address),
                            },
                        };

                        if (!shouldSendQuote) {
                            return [actions.OnlineOrderQuoteDeliveryErrorRequest(), ...(setCartDeliveryAddress ? [actions.CartSetDeliveryAddress({ address })] : [])];
                        }

                        return this._onlineOrdersService
                            .cartSendQuoteDelivery$(model)
                            .pipe(
                                concatMap((response) => [
                                    actions.OnlineOrderShouldRecalculate(),
                                    actions.OnlineOrderQuoteDeliverySuccessRequest({ payload: response }),
                                    ...(setCartDeliveryAddress ? [actions.CartSetDeliveryAddress({ address: response.DeliveryDetails })] : []),
                                ]),
                            );
                    }),
                    catchError(() => of(actions.OnlineOrderQuoteDeliveryErrorRequest())),
                ),
            ),
        ),
    );

    public onOnlineOrderQuoteDeliverySuccessRequest$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderQuoteDeliverySuccessRequest),
            map(() => actions.OnlineOrderRecalculateRequest({ shouldConfirmPreparationTime: false })),
        ),
    );

    constructor(
        @Inject(Tokens.STATIC_TEXT_TOKEN) private _t: T.StaticTexts,
        @Inject(Tokens.CONFIG_TOKEN) private _config: OLO.Config,
        private _actions$: Actions,
        private _onlineOrdersService: Services.OnlineOrdersService,
        private _paymentService: Services.PaymentsService,
        private _cryptoService: Services.CryptoService,
        protected _routeService: Services.RouteService,
        private _modalsServicService: Services.ModalsService,
        private _store: Store<OLO.State>,
    ) {}

    private _getDuplicateOrderFlags(
        action: { model?: APIv3_SNAPSHOT.OnlineOrderDetailedBusinessModel; previousOrderInfo?: OLO.DTO.OnlineOrdersPreviousOrder } & Action,
        orderStatus: OLO.Enums.ONLINE_ORDER_STATUS,
        payment?: APIv3_SNAPSHOT.GetTransactionResponse,
    ): OLO.Common.OrderDuplicationFlags {
        const duplicationFlags: OLO.Common.OrderDuplicationFlags = {};
        const paymentStatus = payment?.Status;

        duplicationFlags.timeDiffInOrders = Utils.Dates.datesDiffInMinutes(new Date(action.model.OrderedDate), new Date(action.previousOrderInfo.OrderedDate));
        duplicationFlags.isSameGross = action.previousOrderInfo.TotalGrossValue === action.model.TotalGrossValue;
        duplicationFlags.isRecentSameOrder = duplicationFlags.timeDiffInOrders <= 5 && duplicationFlags.isSameGross;

        if (payment) {
            duplicationFlags.isDuringPaymentProcedure = orderStatus === OLO.Enums.ONLINE_ORDER_STATUS.CREATED && paymentStatus === OLO.Enums.PAYMENT_STATUS.PENDING;
            duplicationFlags.isAlreadyPayed =
                orderStatus > OLO.Enums.ONLINE_ORDER_STATUS.CREATED && orderStatus < OLO.Enums.ONLINE_ORDER_STATUS.CANCELED && paymentStatus === OLO.Enums.PAYMENT_STATUS.SUCCESS;
            duplicationFlags.isImproperOrder = orderStatus === OLO.Enums.ONLINE_ORDER_STATUS.CREATED && paymentStatus === OLO.Enums.PAYMENT_STATUS.FAILED;
        } else {
            duplicationFlags.isAlreadyPayed = orderStatus > OLO.Enums.ONLINE_ORDER_STATUS.CREATED && orderStatus < OLO.Enums.ONLINE_ORDER_STATUS.CANCELED;
        }

        return duplicationFlags;
    }

    private _getOrderStreamBasedOnAuth$(orderGUID: string): Observable<APIv3_SNAPSHOT.OnlineOrderDetailedBusinessModel> {
        return this._store.pipe(
            select(selectors.isMemberAuthorizedJWT),
            take(1),
            switchMap((isAuthenticated) => (isAuthenticated ? this._onlineOrdersService.getOnlineOrder$(orderGUID) : of({ Status: OLO.Enums.ONLINE_ORDER_STATUS.VALIDATED }))),
            catchError((error) => {
                console.error('Payment status problems', error);

                return of(error);
            }),
        );
    }
}
